import { Component, Input, OnInit, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { KontaktService, Kontakt, KontaktResponse } from '../services/kontakt.service';
import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-kontakte',
  templateUrl: './kontakte.component.html',
  styleUrls: ['./kontakte.component.scss']
})
export class KontakteComponent implements OnInit, OnChanges {
  @Input() kundeId: string;
  @Input() isEmbedded: boolean = false;
  @Input() parentContext?: string;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'name',
    'email',
    'telefon',
    'mobile',
    'ort',
    'status',
    'actions'
  ];

  dataSource: MatTableDataSource<Kontakt>;
  isLoadingResults = true;
  isRateLimitReached = false;
  totalItems = 0;
  pageSizes = [10, 25, 50, 100];
  searchValue = '';

  constructor(
    private kontaktService: KontaktService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.dataSource = new MatTableDataSource<Kontakt>([]);
  }

  ngOnInit() {
    console.log('KontakteComponent: ngOnInit called with kundeId:', this.kundeId);
    console.log('🔍 Component Context:', {
      isEmbedded: this.isEmbedded,
      parentContext: this.parentContext
    });
    
    if (this.isEmbedded) {
      this.showEmbeddedUI();
    } else {
      this.showStandaloneUI();
    }
    
    this.loadKontakte();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['kundeId'] && !changes['kundeId'].firstChange) {
      console.log('KontakteComponent: kundeId changed from', changes['kundeId'].previousValue, 'to', changes['kundeId'].currentValue);
      this.loadKontakte();
    }
  }

  ngAfterViewInit() {
    if (this.paginator && this.sort) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  loadKontakte() {
    if (!this.kundeId) {
      console.warn('No kunde ID provided for contacts loading');
      return;
    }

    console.log('KontakteComponent: Loading contacts for kundeId:', this.kundeId);
    this.isLoadingResults = true;
    
    const page = this.paginator ? this.paginator.pageIndex + 1 : 1;
    const limit = this.paginator ? this.paginator.pageSize : 10;
    const sortBy = this.sort ? this.sort.active : 'name';
    const sortOrder = this.sort ? this.sort.direction : 'asc';

    this.kontaktService.getKontakteByKundeId(this.kundeId, page, limit).subscribe(
      (response: KontaktResponse) => {
        console.log('Kontakte loaded successfully for kundeId:', this.kundeId, 'Response:', response);
        this.dataSource.data = response.data || [];
        this.totalItems = response.total || 0;
        this.isLoadingResults = false;
        this.isRateLimitReached = false;
      },
      (error) => {
        console.error('Error loading kontakte for kundeId:', this.kundeId, 'Error:', error);
        this.isLoadingResults = false;
        this.isRateLimitReached = true;
        this.snackBar.open('Fehler beim Laden der Kontakte', 'Schließen', { duration: 3000 });
      }
    );
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onPageChange() {
    this.loadKontakte();
  }

  onSortChange() {
    this.loadKontakte();
  }

  editKontakt(kontakt: Kontakt) {
    console.log('Edit kontakt:', kontakt);
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      // In embedded mode within client-edit, navigate to kontakt-edit with return context
      this.router.navigate(['/kontakt-edit', kontakt.id], { 
        queryParams: { 
          kundeId: this.kundeId,
          returnTo: 'client-edit'
        } 
      });
    } else {
      // Standalone mode - normal navigation
      this.router.navigate(['/kontakt-edit', kontakt.id], { 
        queryParams: { kundeId: this.kundeId } 
      });
    }
  }

  deleteKontakt(kontakt: Kontakt) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Kontakt löschen',
        message: `Sind Sie sicher, dass Sie den Kontakt "${kontakt.name}" löschen möchten?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.kontaktService.deleteKontakt(kontakt.id).subscribe(
          (response) => {
            this.snackBar.open('Kontakt erfolgreich gelöscht', 'Schließen', { duration: 3000 });
            this.loadKontakte(); // Reload the data
          },
          (error) => {
            console.error('Error deleting kontakt:', error);
            this.snackBar.open('Fehler beim Löschen des Kontakts', 'Schließen', { duration: 5000 });
          }
        );
      }
    });
  }

  addKontakt() {
    console.log('Add new kontakt for kunde:', this.kundeId);
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      // In embedded mode within client-edit, navigate to kontakt-edit with return context
      this.router.navigate(['/kontakt-edit', 'new'], { 
        queryParams: { 
          kundeId: this.kundeId,
          returnTo: 'client-edit'
        } 
      });
    } else {
      // Standalone mode - normal navigation
      this.router.navigate(['/kontakt-edit', 'new'], { 
        queryParams: { kundeId: this.kundeId } 
      });
    }
  }

  getStatusText(statusId: number): string {
    if (!statusId) {
      return 'Kein Status';
    }
    
    const status = (environment as any).kontakt_status?.find((s: any) => s.id === statusId);
    return status ? status.label : `Status ${statusId}`;
  }

  /**
   * Get CSS classes for status badge based on status ID
   */
  getStatusClasses(statusId: number): string {
    if (!statusId) {
      return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
    
    // Return appropriate classes based on status ID
    switch (statusId) {
      case 1:
        return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/50';
      case 2:
        return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 3:
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-500/50';
      case 4:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-500/50';
      default:
        return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
  }

  showEmbeddedUI(): void {
    console.log('🎯 Showing embedded UI for kontakte component');
    // Hide header elements, adjust styling for embedded view
    // This will be handled in the template with conditional rendering
  }

  showStandaloneUI(): void {
    console.log('🎯 Showing standalone UI for kontakte component');
    // Show full header, navigation, etc. for standalone view
    // This will be handled in the template with conditional rendering
  }
}
